import InverterProductionTile from './InverterProductionTile';
import { useMemo, useState } from 'react';
import { Cpu, Gauge, Thermometer, Zap } from 'lucide-react';
import type { InverterHeatmapRow } from '@/api/inverters';

type MetricMode = 'status' | 'power' | 'daily' | 'performance' | 'temperature';

type Props = {
  rows: InverterHeatmapRow[];
  loading?: boolean;
  initialMode?: MetricMode;
  showTitle?: boolean;
  showSummary?: boolean;
  onInverterClick?: (inverterId: number) => void;
};

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function heatColor(value: number, min: number, max: number): string {
  if (max <= min) return '#3ba272';
  const ratio = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const hue = 120 - ratio * 120;
  return `hsl(${hue} 72% 45%)`;
}

function temperatureBandColor(value: number | null): string {
  if (value === null) return '#d6d3d1';
  if (value < 35) return '#3b82f6'; // soguk
  if (value < 45) return '#22c55e'; // normal
  if (value < 55) return '#f59e0b'; // derating bolgesine yakin
  return '#ef4444'; // kritik
}

function powerBandColor(value: number | null): string {
  if (value === null) return '#d6d3d1';
  if (value < 1) return '#94a3b8';
  if (value < 10) return '#ef4444';
  if (value < 30) return '#f59e0b';
  return '#22c55e';
}

function performanceBandColor(value: number | null): string {
  if (value === null) return '#d6d3d1';
  if (value < 40) return '#ef4444';
  if (value < 60) return '#f59e0b';
  if (value < 80) return '#22c55e';
  return '#16a34a';
}

export default function InverterHeatmapPanel({ rows, loading = false, onInverterClick, initialMode = 'power', showTitle = true, showSummary = true }: Props) {
  const [mode, setMode] = useState<MetricMode>(initialMode);

  const stats = useMemo(() => {
    const total = rows.length;
    const online = rows.filter((row) => row.status === 1).length;
    const offline = total - online;
    const warning = rows.filter((row) => row.status === 1 && (() => {
      const performanceRatio = toNumber(row.performance_ratio);
      return performanceRatio !== null && performanceRatio < 60;
    })()).length;
    const totalProductionKw = rows.reduce((sum, row) => sum + (toNumber(row.power_kw) ?? 0), 0);
    const dailyValues = rows.map(row=>toNumber(row.daily_energy_kwh)).filter((value):value is number=>value!==null);
    const dailyEnergyKwh = dailyValues.length ? dailyValues.reduce((sum,value)=>sum+value,0) : null;
    return { total, online, offline, warning, totalProductionKw, dailyEnergyKwh, dailyCount:dailyValues.length };
  }, [rows]);

  const dailyGroups=useMemo(()=>{
    const groups=new Map<string,{key:string;name:string;rows:InverterHeatmapRow[]}>();
    for(const row of rows){
      const key=row.plant_uuid || `${row.plant_id}:${row.plant_name}`;
      if(!groups.has(key))groups.set(key,{key,name:row.plant_name,rows:[]});
      groups.get(key)!.rows.push(row);
    }
    return [...groups.values()].sort((a,b)=>a.name.localeCompare(b.name,'tr') || a.key.localeCompare(b.key)).map(group=>{
      const sorted=group.rows.slice().sort((a,b)=>(toNumber(b.daily_energy_kwh) ?? -Infinity)-(toNumber(a.daily_energy_kwh) ?? -Infinity) || a.inverter_name.localeCompare(b.inverter_name,'tr') || a.inverter_id-b.inverter_id);
      const values=sorted.map(row=>toNumber(row.daily_energy_kwh)).filter((value):value is number=>value!==null);
      return {...group,rows:sorted,minimum:values.length?Math.min(...values):0,maximum:values.length?Math.max(...values):0};
    });
  },[rows]);

  const valueRange = useMemo(() => {
    const source = rows
      .map((row) => {
        if (mode === 'power') return toNumber(row.power_kw);
        if (mode === 'daily') return toNumber(row.daily_energy_kwh);
        if (mode === 'performance') return toNumber(row.performance_ratio);
        if (mode === 'temperature') return toNumber(row.internal_temperature);
        return null;
      })
      .filter((value): value is number => value !== null);

    if (source.length === 0) return { min: 0, max: 0 };
    return {
      min: Math.min(...source),
      max: Math.max(...source),
    };
  }, [rows, mode]);

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className={`mb-4 flex flex-wrap items-center gap-3 ${showTitle ? 'justify-between' : 'justify-end'}`}>
        {showTitle && <h3 className="text-lg font-bold text-slate-900">İnverterlar</h3>}

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {mode === 'temperature' ? (
              <>
                <span className="inline-block h-3 w-3 rounded bg-blue-500" />
                <span>&lt;35°C</span>
                <span className="inline-block h-3 w-3 rounded bg-green-500" />
                <span>35-45°C</span>
                <span className="inline-block h-3 w-3 rounded bg-amber-500" />
                <span>45-55°C</span>
                <span className="inline-block h-3 w-3 rounded bg-red-500" />
                <span>55°C+</span>
              </>
            ) : mode === 'power' ? (
              <>
                <span className="inline-block h-3 w-3 rounded bg-slate-400" />
                <span>&lt;1 kW</span>
                <span className="inline-block h-3 w-3 rounded bg-red-500" />
                <span>1-10 kW</span>
                <span className="inline-block h-3 w-3 rounded bg-amber-500" />
                <span>10-30 kW</span>
                <span className="inline-block h-3 w-3 rounded bg-green-500" />
                <span>30+ kW</span>
              </>
            ) : mode === 'daily' ? (
              <><span>Düşük Üretim</span><span aria-hidden="true" className="h-2.5 w-24 rounded-full bg-gradient-to-r from-blue-50 via-blue-300 to-blue-800"/><span>Yüksek Üretim</span></>
            ) : mode === 'performance' ? (
              <>
                <span className="inline-block h-3 w-3 rounded bg-red-500" />
                <span>&lt;40%</span>
                <span className="inline-block h-3 w-3 rounded bg-amber-500" />
                <span>40-60%</span>
                <span className="inline-block h-3 w-3 rounded bg-green-500" />
                <span>60-80%</span>
                <span className="inline-block h-3 w-3 rounded bg-emerald-600" />
                <span>80%+</span>
              </>
            ) : (
              <>
                <span className="inline-block h-3 w-3 rounded bg-emerald-500" />
                <span>Online</span>
                <span className="inline-block h-3 w-3 rounded bg-amber-500" />
                <span>Uyari</span>
                <span className="inline-block h-3 w-3 rounded bg-slate-300" />
                <span>Offline</span>
              </>
            )}
          </div>

          <div className="inline-flex flex-wrap items-center gap-2">
          {[
            { key: 'status', label: 'Durum' },
            { key: 'performance', label: 'Performance Ratio' },
            { key: 'power', label: 'Anlık Üretim (kW)' },
            { key: 'daily', label: 'Günlük Üretim (kWh)' },
            { key: 'temperature', label: 'Ic Sicaklik (°C)' },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setMode(item.key as MetricMode)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                mode === item.key
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              {item.label}
            </button>
          ))}
          </div>
        </div>
      </div>

      {showSummary && <div className="mb-4 grid grid-cols-5 gap-2">
        <div className="rounded-xl bg-slate-50 px-2 py-2">
          <div className="mb-1 flex items-center gap-1 text-[10px] text-slate-500"><Cpu size={12} /> Toplam</div>
          <div className="text-base font-bold text-slate-900">{stats.total}</div>
        </div>
        <div className="rounded-xl bg-emerald-50 px-2 py-2">
          <div className="mb-1 flex items-center gap-1 text-[10px] text-emerald-700"><Zap size={12} /> Online</div>
          <div className="text-base font-bold text-emerald-700">{stats.online}</div>
        </div>
        <div className="rounded-xl bg-slate-100 px-2 py-2">
          <div className="mb-1 flex items-center gap-1 text-[10px] text-slate-600"><Gauge size={12} /> Offline</div>
          <div className="text-base font-bold text-slate-700">{stats.offline}</div>
        </div>
        <div className="rounded-xl bg-amber-50 px-2 py-2">
          <div className="mb-1 flex items-center gap-1 text-[10px] text-amber-700"><Thermometer size={12} /> Uyari</div>
          <div className="text-base font-bold text-amber-700">{stats.warning}</div>
        </div>
        <div className="rounded-xl bg-sky-50 px-2 py-2" title={mode === 'daily' ? `Bugüne ait sayaç kaydı: ${stats.dailyCount}/${stats.total} inverter` : undefined}>
          <div className="mb-1 flex items-center gap-1 text-[10px] text-sky-700"><Zap size={12} /> {mode === 'daily' ? 'Günlük' : 'Anlık'}</div>
          <div className="text-base font-bold text-sky-700">{mode === 'daily' ? `${stats.dailyEnergyKwh == null ? '—' : stats.dailyEnergyKwh.toLocaleString('tr-TR',{maximumFractionDigits:1})} kWh` : `${stats.totalProductionKw.toFixed(1)} kW`}</div>
        </div>
      </div>}

      {mode === 'daily' ? <div className="space-y-5">
        {loading && !rows.length && <p role="status" className="text-sm text-slate-500">Yükleniyor…</p>}
        {!loading && !rows.length && <p className="text-sm text-slate-500">Gösterilecek inverter verisi bulunamadı.</p>}
        {dailyGroups.map(group=><section key={group.key} aria-label={`${group.name} günlük inverter üretimleri`}>
          <div className="mb-2 flex flex-wrap items-center gap-2 border-b border-slate-100 pb-2"><h4 className="text-sm font-bold text-slate-800">{group.name}</h4><span className="text-xs text-slate-400">{group.rows.length} inverter</span></div>
          <ol aria-label={`${group.name} üretime göre büyükten küçüğe inverterler`} className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-2">
            {group.rows.map(row=><li key={row.inverter_id} className="min-w-0"><InverterProductionTile name={row.inverter_name} productionKwh={toNumber(row.daily_energy_kwh)} minimum={group.minimum} maximum={group.maximum} onClick={()=>onInverterClick?.(row.inverter_id)}/></li>)}
          </ol>
        </section>)}
      </div> : (
      <div className="overflow-x-auto">
        <div className="flex flex-wrap items-center gap-1">
          {loading && rows.length === 0 && <div className="text-sm text-slate-500">Yukleniyor...</div>}
          {!loading && rows.length === 0 && <div className="text-sm text-slate-500">Gosterilecek inverter verisi bulunamadi.</div>}

          {rows.map((row) => {
            const metricValue = mode === 'power'
              ? toNumber(row.power_kw)
              : mode === 'performance'
                ? toNumber(row.performance_ratio)
                : mode === 'temperature'
                  ? toNumber(row.internal_temperature)
                  : null;

            const color = mode === 'status'
              ? (row.status === 1 ? '#3ba272' : '#b0b0a8')
              : mode === 'temperature'
                ? temperatureBandColor(toNumber(row.internal_temperature))
                : mode === 'power'
                  ? powerBandColor(toNumber(row.power_kw))
                  : mode === 'performance'
                    ? performanceBandColor(toNumber(row.performance_ratio))
                : (metricValue === null
                  ? '#d6d3d1'
                  : heatColor(metricValue, valueRange.min, valueRange.max));

            const detailLabel = mode === 'power'
              ? `${toNumber(row.power_kw) ?? '-'} kW`
              : mode === 'performance'
                ? `${toNumber(row.performance_ratio) ?? '-'} %`
                : mode === 'temperature'
                  ? `${toNumber(row.internal_temperature) ?? '-'} °C`
                  : (row.status === 1 ? 'Online' : 'Offline');

            return (
              <button
                key={row.inverter_id}
                type="button"
                onClick={() => onInverterClick?.(row.inverter_id)}
                className="relative h-8 w-8 rounded-md border border-white/70 shadow-sm transition hover:scale-105"
                style={{ backgroundColor: color }}
                title={`Santral: ${row.plant_name}\nInverter: ${row.inverter_name}\nDeger: ${detailLabel}`}
              >
                {(mode === 'temperature' || mode === 'power' || mode === 'performance') && (
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.55)]">
                    {mode === 'temperature'
                      ? (() => {
                        const temp = toNumber(row.internal_temperature);
                        return temp === null ? '-' : Math.round(temp);
                      })()
                      : mode === 'power' ? (() => {
                        const kw = toNumber(row.power_kw);
                        return kw === null ? '-' : kw.toFixed(1);
                      })() : (() => {
                        const pr = toNumber(row.performance_ratio);
                        return pr === null ? '-' : Math.round(pr);
                      })()}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}
